// @ts-nocheck
/**
 * Product Card Gallery Manager (Centralized)
 * 
 * Handles media filtering and hover previews for ALL card types:
 * - Variant cards (collection page)
 * - Regular product cards (all pages)
 * - Recommendation cards
 * 
 * Features:
 * - Smooth hover previews (no click needed)
 * - Centralized media filtering by color (shows ALL media for selected color)
 * - Works for all card types using ProductCardServices
 * 
 * Version: 2025-01-28-centralized-services
 */

import { ProductCardServices } from './product-card-services.js';
import { productCardMediaFilter } from './product-card-media-filter.js';

class VariantCardGallery {
  constructor() {
    this.initialized = new WeakSet();
    this.filterFunctions = new WeakMap(); // Store filter function per card
    this.originalMethods = new WeakMap(); // Store original methods per productCard to prevent overwrites
    this.revealObserver = null; // Single MutationObserver for all cards
    this.slideCache = new WeakMap(); // Cache slide references per card gallery
  }


  /**
   * Initialize all variant cards - 100% optimized with progressive loading
   * Also initializes regular product cards with color swatches (filtered products)
   * AND recommendation cards with color swatches
   */
  init() {
    // Find variant cards (collection page)
    const variantCardGalleries = document.querySelectorAll('[data-collection-variant-id] .card-gallery');
    
    // Find regular product cards with color swatches (filtered products, collection page)
    const regularProductCards = document.querySelectorAll('product-card:not([data-collection-variant-id])');
    /** @type {Element[]} */
    const regularCardGalleries = [];
    regularProductCards.forEach((card) => {
      // Cache queries - query once and reuse
      const gallery = card.querySelector('.card-gallery');
      if (gallery) {
        const variantPicker = card.querySelector('swatches-variant-picker-component');
        // Only include if it has a variant picker with swatches (color options)
        if (variantPicker) {
          regularCardGalleries.push(gallery);
        }
      }
    });
    
    // Find recommendation cards with color swatches (product recommendations section)
    // Optimized: Use single combined selector instead of multiple queries
    const recommendationCards = document.querySelectorAll(
      '.resource-list__item product-card, ' +
      '.resource-list__item .product-card, ' +
      'product-recommendations product-card, ' +
      '.product-recommendations product-card'
    );
    
    /** @type {Element[]} */
    const recommendationCardGalleries = [];
    // Use Set to deduplicate (in case selectors overlap)
    const uniqueCards = new Set(Array.from(recommendationCards));
    uniqueCards.forEach((card) => {
      // Cache queries - query once and reuse
      const gallery = card.querySelector('.card-gallery');
      if (gallery) {
        const variantPicker = card.querySelector('swatches-variant-picker-component');
        // Only include if it has a variant picker with swatches (color options)
        if (variantPicker) {
          recommendationCardGalleries.push(gallery);
        }
      }
    });
    
    // Combine all galleries: variant cards + regular product cards + recommendation cards
    const allGalleries = Array.from(variantCardGalleries)
      .concat(regularCardGalleries)
      .concat(recommendationCardGalleries);
    if (allGalleries.length === 0) {
      return;
    }

    // Initialize all cards - use IntersectionObserver for better performance
    // This ensures cards are initialized when they come into view
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const gallery = entry.target;
            this.initCard(gallery);
            // Unobserve after initialization to avoid re-initializing
            observer.unobserve(gallery);
          }
        });
      }, {
        rootMargin: '100px', // Start loading 100px before visible
        threshold: 0.01
      });
      
      // Observe all galleries
      allGalleries.forEach((gallery) => {
        observer.observe(gallery);
      });
      
      // Also initialize visible cards immediately (first batch)
      const initialBatch = allGalleries.slice(0, 6);
      requestAnimationFrame(() => {
        initialBatch.forEach((gallery) => {
          this.initCard(gallery);
          observer.unobserve(gallery);
        });
      });
    } else {
      // Fallback: Progressive initialization if IntersectionObserver not available
      const initialBatch = allGalleries.slice(0, 6);
      const remainingBatch = allGalleries.slice(6);
      
      requestAnimationFrame(() => {
        initialBatch.forEach((gallery) => {
          this.initCard(gallery);
        });
        
        if (remainingBatch.length > 0) {
          requestAnimationFrame(() => {
            remainingBatch.forEach((gallery) => {
              this.initCard(gallery);
            });
          });
        }
      });
    }
  }

  /**
   * Initialize a single variant card (100% optimized with error handling)
   * @param {Element} cardGallery
   */
  initCard(cardGallery) {
    // Debug removed - see Liquid debug logs instead

    if (this.initialized.has(cardGallery)) {
      return;
    }
    this.initialized.add(cardGallery);

    try {
      const productCard = cardGallery.closest('product-card');
      if (!productCard) {
        cardGallery.setAttribute('data-error', 'true');
        return;
      }

      const card = cardGallery.closest('[data-collection-variant-id]');
      const slideshow = cardGallery.querySelector('slideshow-component');
      
      // For variant cards, slideshow is now enabled for swiper functionality
      // We still need to wait for slideshow to be ready
      
      if (!slideshow) {
        // Retry with exponential backoff (100% reliability)
        let retries = 0;
        const maxRetries = 5;
        const retry = () => {
          if (retries < maxRetries) {
            retries++;
            setTimeout(() => {
              const slideshow = cardGallery.querySelector('slideshow-component');
              if (slideshow) {
                this.continueInit(cardGallery, productCard, slideshow);
              } else {
                retry();
              }
            }, 100 * retries);
          } else {
            cardGallery.setAttribute('data-error', 'true');
          }
        };
        retry();
        return;
      }

      this.continueInit(cardGallery, productCard, slideshow);
    } catch (error) {
      cardGallery.setAttribute('data-error', 'true');
    }
  }

  /**
   * Helper to get visible slides (not hidden)
   * Uses refs.slides to get all slides, then filters
   * @param {any} slideshow
   * @returns {Element[]}
   */
  getVisibleSlides(slideshow) {
    if (!slideshow || !slideshow.refs || !slideshow.refs.slides) return [];
    return Array.from(slideshow.refs.slides).filter(slide => !slide.hidden);
  }
  
  /**
   * Helper to get all slides (including hidden)
   * @param {any} slideshow
   * @returns {Element[]}
   */
  getAllSlides(slideshow) {
    if (!slideshow || !slideshow.refs || !slideshow.refs.slides) return [];
    return Array.from(slideshow.refs.slides);
  }
  
  /**
   * Helper to get next visible slide index
   * @param {any} slideshow
   * @param {number} currentIndex
   * @returns {number}
   */
  getNextVisibleIndex(slideshow, currentIndex) {
    const visibleSlides = this.getVisibleSlides(slideshow);
    if (visibleSlides.length === 0) return currentIndex;
    
    const allSlides = this.getAllSlides(slideshow);
    if (allSlides.length === 0) return currentIndex;
    
    const currentSlide = allSlides[currentIndex];
    if (!currentSlide) {
      // Current index invalid, return first visible
      return visibleSlides[0] ? allSlides.indexOf(visibleSlides[0]) : currentIndex;
    }
    
    // Find current slide's position in visible slides
    const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
    if (currentVisibleIndex === -1) {
      // Current slide is hidden, find first visible
      return visibleSlides[0] ? allSlides.indexOf(visibleSlides[0]) : currentIndex;
    }
    
    // Get next visible slide (with infinite loop)
    const nextVisibleIndex = (currentVisibleIndex + 1) % visibleSlides.length;
    const nextVisibleSlide = visibleSlides[nextVisibleIndex];
    return nextVisibleSlide ? allSlides.indexOf(nextVisibleSlide) : currentIndex;
  }
  
  /**
   * Helper to get previous visible slide index
   * @param {any} slideshow
   * @param {number} currentIndex
   * @returns {number}
   */
  getPreviousVisibleIndex(slideshow, currentIndex) {
    const visibleSlides = this.getVisibleSlides(slideshow);
    if (visibleSlides.length === 0) return currentIndex;
    
    const allSlides = this.getAllSlides(slideshow);
    if (allSlides.length === 0) return currentIndex;
    
    const currentSlide = allSlides[currentIndex];
    if (!currentSlide) {
      // Current index invalid, return last visible
      const lastVisible = visibleSlides[visibleSlides.length - 1];
      return lastVisible ? allSlides.indexOf(lastVisible) : currentIndex;
    }
    
    // Find current slide's position in visible slides
    const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
    if (currentVisibleIndex === -1) {
      // Current slide is hidden, find last visible
      const lastVisible = visibleSlides[visibleSlides.length - 1];
      return lastVisible ? allSlides.indexOf(lastVisible) : currentIndex;
    }
    
    // Get previous visible slide (with infinite loop)
    const prevVisibleIndex = currentVisibleIndex === 0 
      ? visibleSlides.length - 1 
      : currentVisibleIndex - 1;
    const prevVisibleSlide = visibleSlides[prevVisibleIndex];
    return prevVisibleSlide ? allSlides.indexOf(prevVisibleSlide) : currentIndex;
  }
  
  /**
   * Continue initialization after slideshow is ready
   * @param {Element} cardGallery
   * @param {Element} productCard
   * @param {any} slideshow
   */
  continueInit(cardGallery, productCard, slideshow) {
    try {
      const card = cardGallery.closest('[data-collection-variant-id]');
      
      // For variant cards: Override slideshow's next/previous to use filtered visible slides
      if (card && slideshow) {
        // Store reference to this for use in bound functions
        const self = this;
        // CRITICAL: Setup single MutationObserver for all cards (performance optimization)
        // This ensures swiper/touch navigation works even if slideshow removes reveal
        if (!this.revealObserver) {
          this.revealObserver = new MutationObserver((mutations) => {
            // Batch process all mutations for better performance
            const slidesToUpdate = new Set();
            
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && 
                  (mutation.attributeName === 'reveal' || mutation.attributeName === 'hidden')) {
                const slide = mutation.target;
                // Only process if it's an Element and a visible slide
                /** @type {any} */
                const slideElement = slide;
                if (slide instanceof Element && !slideElement.hidden && !slide.hasAttribute('hidden')) {
                  slidesToUpdate.add(slide);
                }
              }
            });
            
            // Batch update all slides at once
            if (slidesToUpdate.size > 0) {
              requestAnimationFrame(() => {
                slidesToUpdate.forEach(slide => {
                  if (!slide.hasAttribute('reveal')) {
                    slide.setAttribute('reveal', '');
                  }
                });
              });
            }
          });
        }
        
        // Cache slides for this gallery to avoid repeated queries
        if (!this.slideCache.has(cardGallery)) {
          const allSlides = Array.from(cardGallery.querySelectorAll('slideshow-slide'));
          this.slideCache.set(cardGallery, allSlides);
          
          // Observe all slides with the single observer
          allSlides.forEach(slide => {
            if (slide && this.revealObserver) {
              this.revealObserver.observe(slide, {
                attributes: true,
                attributeFilter: ['reveal', 'hidden']
              });
            }
          });
        }
        
        // Store original methods
        if (!slideshow._originalNext) {
          slideshow._originalNext = slideshow.next.bind(slideshow);
          slideshow._originalPrevious = slideshow.previous.bind(slideshow);
        }
        
        // Override next() to navigate through filtered visible slides only
        /** @param {any} event @param {any} options */
        slideshow.next = function(event, options) {
          // CRITICAL: Use cached slides for better performance
          const allSlides = self.slideCache.get(cardGallery) || Array.from(cardGallery.querySelectorAll('slideshow-slide'));
          // Update cache if it was empty
          if (!self.slideCache.has(cardGallery) && allSlides.length > 0) {
            self.slideCache.set(cardGallery, allSlides);
          }
          /** @param {any} slide */
          const visibleSlides = allSlides.filter(slide => !(/** @type {any} */ (slide)).hidden && !slide.hasAttribute('hidden'));
          
          if (visibleSlides.length <= 1) {
            return;
          }
          
          // CRITICAL: Ensure all visible slides have reveal attribute BEFORE getting slides array
          // The slideshow's slides getter filters by reveal, so we need reveal on all visible slides
          visibleSlides.forEach(slide => {
            if (!slide.hasAttribute('reveal')) {
              slide.setAttribute('reveal', '');
            }
            slide.removeAttribute('hidden');
            slide.setAttribute('aria-hidden', 'false');
          });
          
          // Get filtered slides array (slideshow filters by reveal)
          const slideshowSlides = this.slides || [];
          
          if (slideshowSlides.length === 0) {
            // No slides in slideshow array, select first visible
            const firstVisible = visibleSlides[0];
            const slideId = firstVisible.getAttribute('slide-id') || firstVisible.getAttribute('data-media-id');
            if (slideId) {
              this.select({ id: slideId }, event, options);
            }
            // Re-add reveal after select removes it (batch update)
            const slidesToReveal = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
            if (slidesToReveal.length > 0) {
              requestAnimationFrame(() => {
                slidesToReveal.forEach(slide => {
                  slide.setAttribute('reveal', '');
                });
              });
            }
            return;
          }
          
          // Get current slide from filtered slides array
          const currentIndex = this.current ?? 0;
          const currentSlide = slideshowSlides[currentIndex];
          
          if (!currentSlide) {
            // Current index invalid, go to first visible
            const firstVisible = visibleSlides[0];
            const slideId = firstVisible.getAttribute('slide-id') || firstVisible.getAttribute('data-media-id');
            if (slideId) {
              this.select({ id: slideId }, event, options);
            }
            // Re-add reveal after select removes it (batch update)
            const slidesToReveal2 = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
            if (slidesToReveal2.length > 0) {
              requestAnimationFrame(() => {
                slidesToReveal2.forEach(slide => {
                  slide.setAttribute('reveal', '');
                });
              });
            }
            return;
          }
          
          // Find current slide's position in visible slides
          const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
          let nextVisibleIndex = 0;
          
          if (currentVisibleIndex >= 0) {
            // Move to next visible slide (with wrap)
            nextVisibleIndex = (currentVisibleIndex + 1) % visibleSlides.length;
          } else {
            // Current slide not in visible list, go to first visible
            nextVisibleIndex = 0;
          }
          
          const nextVisibleSlide = visibleSlides[nextVisibleIndex];
          
          // Select by ID (most reliable)
          const slideId = nextVisibleSlide.getAttribute('slide-id') || nextVisibleSlide.getAttribute('data-media-id');
          if (slideId) {
            this.select({ id: slideId }, event, options);
          } else {
            // Fallback: find index in filtered slides array
            const nextIndexInFiltered = slideshowSlides.indexOf(nextVisibleSlide);
            if (nextIndexInFiltered >= 0) {
              this.select(nextIndexInFiltered, event, options);
            }
          }
          
          // CRITICAL: Re-add reveal immediately after select (select removes it)
          // Batch update for better performance
          const slidesToReveal3 = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
          if (slidesToReveal3.length > 0) {
            requestAnimationFrame(() => {
              /** @param {Element} slide */
              slidesToReveal3.forEach(slide => {
                slide.setAttribute('reveal', '');
              });
            });
          }
        }.bind(slideshow);
        
        // Override previous() to navigate through filtered visible slides only
        /** @param {any} event @param {any} options */
        slideshow.previous = function(event, options) {
          // CRITICAL: Use cached slides for better performance
          const allSlides = self.slideCache.get(cardGallery) || Array.from(cardGallery.querySelectorAll('slideshow-slide'));
          // Update cache if it was empty
          if (!self.slideCache.has(cardGallery) && allSlides.length > 0) {
            self.slideCache.set(cardGallery, allSlides);
          }
          const visibleSlides = allSlides.filter(slide => !slide.hidden && !slide.hasAttribute('hidden'));
          
          if (visibleSlides.length <= 1) {
            return;
          }
          
          // CRITICAL: Ensure all visible slides have reveal attribute BEFORE getting slides array
          // The slideshow's slides getter filters by reveal, so we need reveal on all visible slides
          visibleSlides.forEach(slide => {
            if (!slide.hasAttribute('reveal')) {
              slide.setAttribute('reveal', '');
            }
            slide.removeAttribute('hidden');
            slide.setAttribute('aria-hidden', 'false');
          });
          
          // Get filtered slides array (slideshow filters by reveal)
          const slideshowSlides = this.slides || [];
          
          if (slideshowSlides.length === 0) {
            // No slides in slideshow array, select last visible
            const lastVisible = visibleSlides[visibleSlides.length - 1];
            const slideId = lastVisible.getAttribute('slide-id') || lastVisible.getAttribute('data-media-id');
            if (slideId) {
              this.select({ id: slideId }, event, options);
            }
            // Re-add reveal after select removes it (batch update)
            const slidesToReveal = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
            if (slidesToReveal.length > 0) {
              requestAnimationFrame(() => {
                slidesToReveal.forEach(slide => {
                  slide.setAttribute('reveal', '');
                });
              });
            }
            return;
          }
          
          // Get current slide from filtered slides array
          const currentIndex = this.current ?? 0;
          const currentSlide = slideshowSlides[currentIndex];
          
          if (!currentSlide) {
            // Current index invalid, go to last visible
            const lastVisible = visibleSlides[visibleSlides.length - 1];
            const slideId = lastVisible.getAttribute('slide-id') || lastVisible.getAttribute('data-media-id');
            if (slideId) {
              this.select({ id: slideId }, event, options);
            }
            // Re-add reveal after select removes it (batch update)
            const slidesToReveal2 = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
            if (slidesToReveal2.length > 0) {
              requestAnimationFrame(() => {
                slidesToReveal2.forEach(slide => {
                  slide.setAttribute('reveal', '');
                });
              });
            }
            return;
          }
          
          // Find current slide's position in visible slides
          const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
          let prevVisibleIndex = 0;
          
          if (currentVisibleIndex >= 0) {
            // Move to previous visible slide (with wrap)
            prevVisibleIndex = currentVisibleIndex === 0 ? visibleSlides.length - 1 : currentVisibleIndex - 1;
          } else {
            // Current slide not in visible list, go to last visible
            prevVisibleIndex = visibleSlides.length - 1;
          }
          
          const prevVisibleSlide = visibleSlides[prevVisibleIndex];
          
          // Select by ID (most reliable)
          const slideId = prevVisibleSlide.getAttribute('slide-id') || prevVisibleSlide.getAttribute('data-media-id');
          if (slideId) {
            this.select({ id: slideId }, event, options);
          } else {
            // Fallback: find index in filtered slides array
            const prevIndexInFiltered = slideshowSlides.indexOf(prevVisibleSlide);
            if (prevIndexInFiltered >= 0) {
              this.select(prevIndexInFiltered, event, options);
            }
          }
          
          // CRITICAL: Re-add reveal immediately after select (select removes it)
          // Batch update for better performance
          const slidesToReveal = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
          if (slidesToReveal.length > 0) {
            requestAnimationFrame(() => {
              slidesToReveal.forEach(slide => {
                slide.setAttribute('reveal', '');
              });
            });
          }
        }.bind(slideshow);
      }
      
      // Use arrow function to preserve 'this' context for private method access
      const waitForVariantPicker = () => {
        const variantPicker = productCard.querySelector('swatches-variant-picker-component');
        
        // For variant cards, variant picker is optional (may not exist)
        if (!variantPicker && !card) {
          // Retry with timeout to prevent infinite loops (only for regular cards)
          let retries = 0;
          const maxRetries = 10;
          const retry = () => {
            if (retries < maxRetries) {
              retries++;
              setTimeout(() => {
                const picker = productCard.querySelector('swatches-variant-picker-component');
                if (picker) {
                  // Use 'this' directly in arrow function - it's preserved from outer scope
                  this.setupMediaFiltering(productCard, slideshow, picker, cardGallery);
                  // Ensure first visible slide is selected (Liquid already filtered)
                  this.ensureFirstSlideSelected(slideshow, cardGallery);
                } else {
                  retry();
                }
              }, 50);
            } else {
              cardGallery.setAttribute('data-error', 'true');
            }
          };
          retry();
          return;
        }

        // Setup media filtering and hover previews
        // Use 'this' directly in arrow function - it's preserved from outer scope
        this.setupMediaFiltering(productCard, slideshow, variantPicker, cardGallery);
        
        // Ensure first visible slide is selected initially (Liquid already filtered)
        this.ensureFirstSlideSelected(slideshow, cardGallery);
      };

      waitForVariantPicker();
    } catch (error) {
      cardGallery.setAttribute('data-error', 'true');
    }
  }

  /**
   * Setup media filtering - For variant cards, filter by color and allow color changes via swatch
   * @param {Element} productCard
   * @param {any} slideshow
   * @param {any} variantPicker
   * @param {Element} cardGallery
   */
  setupMediaFiltering(productCard, slideshow, variantPicker, cardGallery) {
    // Debug removed - see Liquid debug logs instead

    const card = productCard.closest('[data-collection-variant-id]');
    
    if (card) {
      // Debug removed - see Liquid debug logs instead

      // VARIANT CARD: Filter media by color, and allow updates when user changes color via swatch
      const normalizeString = (str) => {
        if (!str) return '';
        return str.toLowerCase().replace(/[\s\-_]/g, '');
      };

      // CRITICAL: Get the correct variant ID and color from data attributes set by JavaScript
      // JavaScript reads the parent <li>'s data-variant-id and stores it in data-correct-variant-id
      const correctVariantId = cardGallery.getAttribute('data-correct-variant-id');
      const correctVariantOption1 = cardGallery.getAttribute('data-correct-variant-option1');
      const liquidVariantId = cardGallery.getAttribute('data-liquid-variant-id');
      const needsCorrection = cardGallery.getAttribute('data-variant-needs-correction') === 'true';
      
      // Use correct variant if available, otherwise fall back to card's variant
      const variantId = correctVariantId || card.dataset.collectionVariantId || card.dataset.variantId || '';
      const initialColorValue = correctVariantOption1 || card.dataset.collectionVariantColor || card.dataset.variantColorName || '';
      
      // Debug removed - see Liquid debug logs instead

      /**
       * @param {string} colorValue
       */
      const filterMediaByColor = (colorValue) => {
        // Debug removed - see Liquid debug logs instead

        if (!colorValue) {
          return;
        }

        if (!slideshow) {
          return;
        }

        // UNIFIED: Use centralized media filter for ALL cards
        // This ensures variant cards and regular cards work the same way
        try {
          productCardMediaFilter.filterMediaByColor(cardGallery, slideshow, colorValue, 'variant');
        } catch (error) {
          console.error('[VariantCardGallery] Error calling filterMediaByColor', error);
        }
      };

      this.filterFunctions.set(cardGallery, filterMediaByColor);

      // Initialize with current variant color (from data attribute or variant picker)
      // NOTE: Liquid should already have filtered media correctly, so we only enhance if needed
      // TEMPORARILY DISABLED: Testing Liquid-only filtering
      const initFilter = () => {
        // JavaScript filtering DISABLED - using Liquid-only filtering
        return; // Skip all JavaScript filtering
        
        // Check if Liquid already filtered correctly
        const selectedColorMediaCount = cardGallery.getAttribute('data-selected-color-media-count');
        const hasLiquidMatches = selectedColorMediaCount && parseInt(selectedColorMediaCount, 10) > 0;
        
        // If Liquid found matches, don't re-filter (it's already correct)
        if (hasLiquidMatches) {
          return;
        }
        
        // CRITICAL: Use corrected variant color if available
        const correctVariantOption1 = cardGallery.getAttribute('data-correct-variant-option1');
        const variantId = correctVariantId || card.dataset.collectionVariantId || card.dataset.variantId || '';
        const variantColor = correctVariantOption1 || card.dataset.collectionVariantColor || card.dataset.variantColorName || card.dataset.variantColor || '';
        const selectedOption = variantPicker?.selectedOption;
        const colorValue = variantColor || selectedOption?.value || '';
        
        if (colorValue) {
          // Only filter if Liquid didn't find matches
          filterMediaByColor(colorValue);
        } else {
          // If no color value, filter by any visible slides that Liquid has already set
          const slides = cardGallery.querySelectorAll('slideshow-slide');
          if (slides && slides.length > 0) {
            requestAnimationFrame(() => {
              // Hide all slides that don't have reveal attribute (Liquid sets reveal on matching slides)
              slides.forEach(slide => {
                if (!slide.hasAttribute('reveal') && !slide.hasAttribute('data-initial-visible')) {
                  slide.hidden = true;
                  slide.setAttribute('hidden', 'hidden');
                  slide.style.display = 'none';
                  slide.style.opacity = '0';
                  slide.style.visibility = 'hidden';
                  slide.setAttribute('aria-hidden', 'true');
                } else {
                  slide.setAttribute('data-initial-visible', 'true');
                }
              });
              
              const visibleSlides = Array.from(slides).filter(slide => !slide.hidden && !slide.hasAttribute('hidden'));
              
              // If no visible slides, show first slide as fallback
              let slideToShow = visibleSlides[0];
              if (!slideToShow && slides.length > 0) {
                slideToShow = slides[0];
                slideToShow.hidden = false;
                slideToShow.removeAttribute('hidden');
                slideToShow.style.display = '';
                slideToShow.style.opacity = '';
                slideToShow.style.visibility = '';
                slideToShow.setAttribute('aria-hidden', 'false');
              }
              
              if (slideToShow && slideshow && slideshow.select) {
                const firstIndex = Array.from(slides).indexOf(slideToShow);
                const slideId = slideToShow.getAttribute('slide-id') || slideToShow.getAttribute('data-media-id');
                if (slideId) {
                  slideshow.select({ id: slideId }, undefined, { animate: false });
                } else if (firstIndex >= 0) {
                  slideshow.select(firstIndex, undefined, { animate: false });
                }
              }
            });
          } else {
            requestAnimationFrame(initFilter);
          }
        }
      };
      setTimeout(() => requestAnimationFrame(initFilter), 100);

      // Listen for color changes via swatch (user can change color manually)
      // RE-ENABLED: JavaScript filtering for swatch changes
      /** @param {any} e */
      productCard.addEventListener('variant:update', (e) => {
        // Try to get color value from event data first
        let colorValue = e.detail?.data?.colorValue;
        
        // If not in event data, extract from the selected swatch
        if (!colorValue && e.target) {
          const selectedSwatch = variantPicker?.querySelector('input[type="radio"]:checked');
          if (selectedSwatch) {
            colorValue = selectedSwatch.value || '';
          }
        }
        
        // Fallback: get from variant picker's selected option
        if (!colorValue && variantPicker?.selectedOption) {
          colorValue = variantPicker.selectedOption.value || '';
        }
        
        if (colorValue) {
          filterMediaByColor(colorValue);
        }
      }, { capture: true });

      variantPicker.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.closest('.variant-option--swatches')) {
          const colorValue = e.target.value || '';
          if (colorValue) {
            filterMediaByColor(colorValue);
          }
        }
      }, { capture: true });
      
      // Also listen for click events on swatches (for variant cards)
      // RE-ENABLED: JavaScript filtering for swatch clicks
      /** @param {any} e */
      variantPicker.addEventListener('click', (e) => {
        if (e.target.type === 'radio' && e.target.closest('.variant-option--swatches')) {
          const colorValue = e.target.value || '';
          if (colorValue) {
            // Small delay to ensure the radio is checked
            setTimeout(() => {
              filterMediaByColor(colorValue);
            }, 10);
          }
        }
      }, { capture: true });
    } else {
      // UNIFIED: Regular product cards now use centralized media filter
      // This ensures all cards work the same way at variant level
      const filterMediaByColor = (colorValue) => {
        if (!colorValue || !slideshow) {
          return;
        }
        
        // Use centralized media filter for ALL cards
        productCardMediaFilter.filterMediaByColor(cardGallery, slideshow, colorValue, 'regular');
      };

      this.filterFunctions.set(cardGallery, filterMediaByColor);

      // Initialize with selected color
      // TEMPORARILY DISABLED: Testing Liquid-only filtering
      const initFilter = () => {
        return; // Skip all JavaScript filtering
        
        const selectedOption = variantPicker?.selectedOption;
        if (selectedOption) {
          const colorValue = selectedOption.value || '';
          if (colorValue) {
            const slides = slideshow.querySelectorAll('slideshow-slide');
            if (slides && slides.length > 0) {
              filterMediaByColor(colorValue);
            } else {
              requestAnimationFrame(initFilter);
            }
          }
        } else {
          requestAnimationFrame(initFilter);
        }
      };
      setTimeout(() => requestAnimationFrame(initFilter), 100);

      // Listen for color changes
      // RE-ENABLED: JavaScript filtering for swatch changes on regular cards
      /** @param {any} e */
      productCard.addEventListener('variant:update', (e) => {
        // Try to get color value from event data first
        let colorValue = e.detail?.data?.colorValue;
        
        // If not in event data, extract from the selected swatch
        if (!colorValue && variantPicker?.selectedOption) {
          colorValue = variantPicker.selectedOption.value || '';
        }
        
        if (colorValue) {
          filterMediaByColor(colorValue);
        }
      }, { capture: true });

      /** @param {any} e */
      variantPicker.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.closest('.variant-option--swatches')) {
          const colorValue = e.target.value || '';
          if (colorValue) {
            filterMediaByColor(colorValue);
          }
        }
      }, { capture: true });
    }

    // For variant cards: Setup hover to show next media (no carousel/swiper)
    // Note: 'card' is already declared at the top of setupMediaFiltering
    if (card) {
      // Store original method per productCard to prevent overwrites
      if (!this.originalMethods.has(productCard)) {
        this.originalMethods.set(productCard, {
          previewImage: productCard.previewImage,
          resetImage: productCard.resetImage
        });
      }
      
      const originalMethods = this.originalMethods.get(productCard);
      
      // Track current visible slide index for variant cards
      if (!cardGallery.dataset.currentSlideIndex) {
        cardGallery.dataset.currentSlideIndex = '0';
      }
      
      // Override previewImage for card hover - show next visible media
      // CRITICAL: Disable hover on touch devices (mobile/iPad) - only swiper should work
      const originalPreviewImage = originalMethods.previewImage;
      if (originalPreviewImage) {
        // Helper to detect touch devices
        const isTouchDevice = () => {
          // Check for touch support
          const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
          // Check screen width (mobile/iPad typically < 1025px)
          const isMobileWidth = window.innerWidth < 1025;
          return hasTouch || isMobileWidth;
        };
        
        productCard.previewImage = (event) => {
          try {
            // Disable hover on touch devices - only swiper should work
            if (isTouchDevice()) {
              return; // Don't do anything on touch devices
            }
            
            // Also check pointerType as additional safeguard
            if (event && event.pointerType !== 'mouse') {
              return originalPreviewImage.call(productCard, event);
            }
            
            // Preload next image before showing to prevent flicker
            const allSlides = cardGallery.querySelectorAll('slideshow-slide');
            const visibleSlides = Array.from(allSlides).filter(slide => !slide.hidden && !slide.hasAttribute('hidden'));
            
            if (visibleSlides.length <= 1) {
              // Only one image, nothing to hover
              return;
            }
            
            // Find current and next slide to preload
            const currentSlide = visibleSlides.find(slide => slide.getAttribute('aria-hidden') === 'false' || !slide.hasAttribute('aria-hidden'));
            const currentIndex = currentSlide ? visibleSlides.indexOf(currentSlide) : 0;
            const nextIndex = (currentIndex + 1) % visibleSlides.length;
            const nextSlide = visibleSlides[nextIndex];
            
            // Preload next image to prevent flicker
            if (nextSlide) {
              const nextImage = nextSlide.querySelector('img[loading="lazy"]');
              if (nextImage) {
                // Preload the image
                if (nextImage.dataset.src) {
                  nextImage.src = nextImage.dataset.src;
                  nextImage.removeAttribute('data-src');
                }
                nextImage.removeAttribute('loading');
                
                // Force image load if not already loaded
                if (!nextImage.complete) {
                  const img = new Image();
                  img.src = nextImage.src;
                  img.srcset = nextImage.srcset;
                  img.sizes = nextImage.sizes;
                }
              }
            }
            
            // For variant cards: use slideshow component to show next visible slide
            requestAnimationFrame(() => {
              const slideshow = cardGallery.querySelector('slideshow-component');
              if (!slideshow) {
                return;
              }
              
              if (allSlides.length === 0) {
                return;
              }
              
              // CRITICAL: Ensure all visible slides have reveal attribute BEFORE getting slides array
              visibleSlides.forEach(slide => {
                if (!slide.hasAttribute('reveal')) {
                  slide.setAttribute('reveal', '');
                }
                slide.removeAttribute('hidden');
                slide.setAttribute('aria-hidden', 'false');
              });
              
              // Get filtered slides array (slideshow filters by reveal)
              const slideshowSlides = slideshow.slides || [];
              
              if (slideshowSlides.length === 0) {
                // No slides in slideshow array, select first visible
                const firstVisible = visibleSlides[0];
                const slideId = firstVisible.getAttribute('slide-id') || firstVisible.getAttribute('data-media-id');
                if (slideId && slideshow.select) {
                  slideshow.select({ id: slideId }, undefined, { animate: true });
                }
                // Re-add reveal after select removes it (batch update)
                const slidesToReveal = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
                if (slidesToReveal.length > 0) {
                  requestAnimationFrame(() => {
                    slidesToReveal.forEach(slide => {
                      slide.setAttribute('reveal', '');
                    });
                  });
                }
                return;
              }
              
              // Get current slide from filtered slides array
              const currentIndex = slideshow.current ?? 0;
              const currentSlide = slideshowSlides[currentIndex];
              
              if (!currentSlide) {
                // Current index invalid, go to first visible
                const firstVisible = visibleSlides[0];
                const slideId = firstVisible.getAttribute('slide-id') || firstVisible.getAttribute('data-media-id');
                if (slideId && slideshow.select) {
                  slideshow.select({ id: slideId }, undefined, { animate: true });
                }
                // Re-add reveal after select removes it (batch update)
                const slidesToReveal2 = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
                if (slidesToReveal2.length > 0) {
                  requestAnimationFrame(() => {
                    slidesToReveal2.forEach(slide => {
                      slide.setAttribute('reveal', '');
                    });
                  });
                }
                return;
              }
              
              // CENTRALIZED: Find next slide using ProductCardServices
              const currentVisibleIndex = visibleSlides.indexOf(currentSlide);
              let nextVisibleSlide = null;
              
              // PRIORITY: Look for position -99 (hover image) using centralized service
              const position99Slide = visibleSlides.find(slide => {
                let position = slide.getAttribute('data-media-position');
                if (!position || position === '999') {
                  // Extract position from filename if not in data attribute
                  const img = slide.querySelector('img');
                  const mediaSrc = slide.getAttribute('data-media-src') ||
                                 img?.getAttribute('src') || 
                                 img?.getAttribute('data-src') || '';
                  if (mediaSrc) {
                    position = ProductCardServices.extractPositionFromFilename(mediaSrc).toString();
                    slide.setAttribute('data-media-position', position);
                  }
                }
                return position === '99';
              });
              
              if (position99Slide) {
                // If we have position -99, show it on hover (unless we're already on it)
                const position99Index = visibleSlides.indexOf(position99Slide);
                if (currentVisibleIndex !== position99Index) {
                  nextVisibleSlide = position99Slide;
                } else {
                  // Already on position -99, go to first slide (position -1)
                  const position1Slide = visibleSlides.find(slide => {
                    let position = slide.getAttribute('data-media-position');
                    if (!position || position === '999') {
                      const img = slide.querySelector('img');
                      const mediaSrc = slide.getAttribute('data-media-src') ||
                                     img?.getAttribute('src') || 
                                     img?.getAttribute('data-src') || '';
                      if (mediaSrc) {
                        position = ProductCardServices.extractPositionFromFilename(mediaSrc).toString();
                        slide.setAttribute('data-media-position', position);
                      }
                    }
                    return position === '1';
                  });
                  nextVisibleSlide = position1Slide || visibleSlides[0];
                }
              } else if (currentVisibleIndex >= 0) {
                // No position -99 found, move to next visible slide (with wrap)
                const nextVisibleIndex = (currentVisibleIndex + 1) % visibleSlides.length;
                nextVisibleSlide = visibleSlides[nextVisibleIndex];
              } else {
                // Current slide not visible, go to first visible
                nextVisibleSlide = visibleSlides[0];
              }
              
              if (slideshow.select) {
                // Get slide ID for more reliable selection
                const slideId = nextVisibleSlide.getAttribute('slide-id') || nextVisibleSlide.getAttribute('data-media-id');
                
                // Try selecting by ID first (most reliable)
                if (slideId) {
                  slideshow.select({ id: slideId }, undefined, { animate: true });
                } else {
                  // Fallback: find index in filtered slides array
                  const nextIndexInFiltered = slideshowSlides.indexOf(nextVisibleSlide);
                  if (nextIndexInFiltered >= 0) {
                    slideshow.select(nextIndexInFiltered, undefined, { animate: true });
                  }
                }
                
                // CRITICAL: Re-add reveal immediately after select (select removes it)
                // Batch update for better performance
                const slidesToReveal = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
                if (slidesToReveal.length > 0) {
                  requestAnimationFrame(() => {
                    slidesToReveal.forEach(slide => {
                      slide.setAttribute('reveal', '');
                    });
                  });
                }
              }
            });
          } catch (error) {
            if (originalPreviewImage) {
              originalPreviewImage.call(productCard, event);
            }
          }
        };
      }
      
      // Override resetImage to go back to first visible slide
      // CRITICAL: Disable reset on touch devices (mobile/iPad) - only swiper should work
      const originalResetImage = originalMethods.resetImage;
      if (originalResetImage) {
        // Helper to detect touch devices
        const isTouchDevice = () => {
          // Check for touch support
          const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
          // Check screen width (mobile/iPad typically < 1025px)
          const isMobileWidth = window.innerWidth < 1025;
          return hasTouch || isMobileWidth;
        };
        
        productCard.resetImage = () => {
          try {
            // Disable reset on touch devices - only swiper should work
            if (isTouchDevice()) {
              return; // Don't do anything on touch devices
            }
            
            requestAnimationFrame(() => {
              const slideshow = cardGallery.querySelector('slideshow-component');
              if (!slideshow) {
                return;
              }
              
              // Use cached slides for better performance
              const allSlides = this.slideCache.get(cardGallery) || Array.from(cardGallery.querySelectorAll('slideshow-slide'));
              // Update cache if it was empty
              if (!this.slideCache.has(cardGallery) && allSlides.length > 0) {
                this.slideCache.set(cardGallery, allSlides);
              }
              const visibleSlides = Array.from(allSlides).filter(slide => !slide.hidden && !slide.hasAttribute('hidden'));
              
              if (visibleSlides.length === 0) {
                return;
              }
              
              const firstVisibleSlide = visibleSlides[0];
              
              // CRITICAL: Ensure all visible slides have reveal attribute BEFORE getting slides array
              visibleSlides.forEach(slide => {
                if (!slide.hasAttribute('reveal')) {
                  slide.setAttribute('reveal', '');
                }
                slide.removeAttribute('hidden');
                slide.setAttribute('aria-hidden', 'false');
              });
              
              // Get filtered slides array
              const slideshowSlides = slideshow.slides || [];
              
              if (slideshow.select) {
                // Select by ID for reliability
                const slideId = firstVisibleSlide.getAttribute('slide-id') || firstVisibleSlide.getAttribute('data-media-id');
                if (slideId) {
                  slideshow.select({ id: slideId }, undefined, { animate: true });
                } else {
                  // Fallback: find index in filtered slides array
                  const firstIndexInFiltered = slideshowSlides.indexOf(firstVisibleSlide);
                  if (firstIndexInFiltered >= 0) {
                    slideshow.select(firstIndexInFiltered, undefined, { animate: true });
                  }
                }
                
                // CRITICAL: Re-add reveal immediately after select (select removes it)
                // Batch update for better performance
                const slidesToReveal = visibleSlides.filter(slide => !slide.hasAttribute('reveal'));
                if (slidesToReveal.length > 0) {
                  requestAnimationFrame(() => {
                    slidesToReveal.forEach(slide => {
                      slide.setAttribute('reveal', '');
                    });
                  });
                }
              }
            });
          } catch (error) {
            if (originalResetImage) {
              originalResetImage.call(productCard);
            }
          }
        };
      }
    } else {
      // Regular product cards: use original hover behavior
      if (!this.originalMethods.has(productCard)) {
        this.originalMethods.set(productCard, {
          previewImage: productCard.previewImage,
          resetImage: productCard.resetImage
        });
      }
    }
  }

  /**
   * Ensure first visible slide is selected - 100% reliable helper
   * @param {any} slideshow
   * @param {Element} cardGallery
   */
  ensureFirstSlideSelected(slideshow, cardGallery) {
    requestAnimationFrame(() => {
      const card = cardGallery.closest('[data-collection-variant-id]');
      // Use cached slides for better performance
      const allSlides = this.slideCache.get(cardGallery) || Array.from(cardGallery.querySelectorAll('slideshow-slide'));
      // Update cache if it was empty
      if (!this.slideCache.has(cardGallery) && allSlides.length > 0) {
        this.slideCache.set(cardGallery, allSlides);
      }
      
      if (allSlides.length === 0) {
        // Retry if slides not ready
        setTimeout(() => this.ensureFirstSlideSelected(slideshow, cardGallery), 50);
        return;
      }
      
      // For variant cards: use slideshow component (now enabled for swiper)
      if (card && slideshow && slideshow.select) {
        const visibleSlides = Array.from(allSlides).filter(slide => !slide.hidden);
        
        // If no visible slides, show first slide as fallback
        let slideToShow = visibleSlides[0];
        if (!slideToShow && allSlides.length > 0) {
          slideToShow = allSlides[0];
          // Make it visible
          slideToShow.hidden = false;
          slideToShow.removeAttribute('hidden');
        }
        
        if (!slideToShow) {
          cardGallery.classList.add('gallery-initialized');
          return;
        }
        
        const firstIndex = Array.from(allSlides).indexOf(slideToShow);
        if (firstIndex >= 0) {
          slideshow.current = firstIndex;
          slideshow.select(firstIndex, undefined, { animate: false });
        }
      } else {
        // Regular cards: use slideshow
        const firstVisibleIndex = Array.from(allSlides).findIndex((slide) => !slide.hidden);
        if (firstVisibleIndex >= 0 && slideshow && slideshow.select) {
          slideshow.current = firstVisibleIndex;
          slideshow.select(firstVisibleIndex, undefined, { animate: false });
        }
      }
      
      cardGallery.classList.add('gallery-initialized');
    });
  }

  /**
   * Update media for selected color (only for regular product cards, not variant cards)
   */
  updateMediaForSelectedColor(productCard, slideshow, variantPicker) {
    // Skip for variant cards - Liquid already filtered, don't override
    const card = productCard.closest('[data-collection-variant-id]');
    if (card) return; // Variant cards are already filtered by Liquid
    
    const selectedOption = variantPicker.selectedOption;
    if (!selectedOption) return;
    
    let selectedColor = '';
    if (selectedOption instanceof HTMLInputElement) {
      selectedColor = selectedOption.value || '';
    } else if (selectedOption.value) {
      selectedColor = selectedOption.value;
    }
    
    if (!selectedColor) return;
    const filterFunction = this.filterFunctions.get(
      productCard.querySelector('.card-gallery')
    );

    if (filterFunction) {
      filterFunction(selectedColor);
      requestAnimationFrame(() => {
        const allSlides = slideshow.querySelectorAll('slideshow-slide');
        const firstVisibleIndex = Array.from(allSlides).findIndex((slide) => !slide.hidden);
        if (firstVisibleIndex >= 0 && slideshow.select) {
          slideshow.current = firstVisibleIndex;
          slideshow.select(firstVisibleIndex, undefined, { animate: true });
        }
      });
    } else {
      this.filterMediaDirectly(slideshow, selectedColor);
      requestAnimationFrame(() => {
        const allSlides = slideshow.querySelectorAll('slideshow-slide');
        const firstVisibleIndex = Array.from(allSlides).findIndex((slide) => !slide.hidden);
        if (firstVisibleIndex >= 0 && slideshow.select) {
          slideshow.current = firstVisibleIndex;
          slideshow.select(firstVisibleIndex, undefined, { animate: true });
        }
      });
    }
  }


  /**
   * Direct media filtering (fallback - optimized)
   */
  /**
   * @param {any} slideshow
   * @param {string} colorValue
   */
  filterMediaDirectly(slideshow, colorValue) {
    if (!colorValue || !slideshow) return;

    try {
      /** @param {string} str */
      const normalizeString = (str) => {
        if (!str) return '';
        return str.toLowerCase().replace(/[\s\-_]/g, '');
      };

        // Helper function to extract color from filename (SKU-COLOR-sequence)
        /**
         * @param {string} src
         * @returns {string}
         */
        const extractColorFromFilename = (src) => {
        if (!src) return '';
        try {
          const urlParts = src.split('/');
          const filename = urlParts[urlParts.length - 1].split('?')[0];
          const filenameWithoutExt = filename.split('.')[0];
          const normalizedFilename = filenameWithoutExt.toLowerCase();
          const parts = normalizedFilename.split('-');
          if (parts.length >= 3) {
            const color = parts[1].trim().replace(/[\s\-_]/g, '');
            return color;
          }
        } catch (e) {
          // Silent error handling
        }
        return '';
      };

      const normalizedColor = normalizeString(colorValue);
      const slides = slideshow.querySelectorAll('slideshow-slide');
      if (!slides.length) return;

      let visibleCount = 0;
      let firstVisibleIndex = -1;

      requestAnimationFrame(() => {
        /** @param {Element} slide @param {number} index */
        slides.forEach((slide, index) => {
          // Get media color from data attribute first (set by Liquid), then extract from filename
          let mediaColorFromFilename = slide.getAttribute('data-media-color') || '';
          
          // If not in data attribute, extract from filename
          if (!mediaColorFromFilename) {
            // Get media src from img element or data attribute
            const img = slide.querySelector('img');
            const mediaSrc = slide.getAttribute('data-media-src') ||
                           img?.getAttribute('src') || 
                           img?.getAttribute('data-src') || '';
            
            if (mediaSrc && !slide.getAttribute('data-media-src')) {
              slide.setAttribute('data-media-src', mediaSrc);
            }
            
            // Extract color from filename
            mediaColorFromFilename = extractColorFromFilename(mediaSrc);
            
            // Store extracted color in data attribute for future use
            if (mediaColorFromFilename) {
              slide.setAttribute('data-media-color', mediaColorFromFilename);
            }
          }
          
          const normalizedMediaColor = normalizeString(mediaColorFromFilename);
          const matches = normalizedMediaColor && normalizedColor && (
            normalizedMediaColor === normalizedColor
          );

          // Use hidden attribute for better performance
          slide.hidden = !matches;
          
          if (matches) {
            visibleCount++;
            if (firstVisibleIndex === -1) {
              firstVisibleIndex = index;
            }
          }
        });

        if (visibleCount > 0 && firstVisibleIndex >= 0 && slideshow.select) {
          slideshow.current = firstVisibleIndex;
          slideshow.select(firstVisibleIndex, undefined, { animate: false });
        } else if (visibleCount === 0 && slides.length > 0 && slideshow.select) {
          // Fallback: show first slide if no matches
          slides[0].hidden = false;
          slideshow.current = 0;
          slideshow.select(0, undefined, { animate: false });
        }
      });
    } catch (error) {
      // Silent error handling
    }
  }
}

// Initialize
const variantCardGallery = new VariantCardGallery();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    variantCardGallery.init();
  });
} else {
  // Add a small delay to ensure DOM is fully ready
  setTimeout(() => {
    variantCardGallery.init();
  }, 100);
}

// Watch for dynamically added cards (variant cards, regular product cards, and recommendation cards with swatches)
// Use shared observer pattern to reduce overhead - both files can use the same observer
if (window.MutationObserver) {
  // Initialize shared observer manager if it doesn't exist
  if (!window.__variantCardsObserverManager) {
    window.__variantCardsObserverManager = {
      observer: null,
      handlers: [],
      setup() {
        if (this.observer) return;
        
        this.observer = new MutationObserver((mutations) => {
          // Call all registered handlers
          this.handlers.forEach(handler => {
            try {
              handler(mutations);
            } catch (e) {
              // Silent error handling
            }
          });
        });
        
        const observeBody = () => {
          if (document.body) {
            this.observer.observe(document.body, {
              childList: true,
              subtree: true
            });
          } else {
            setTimeout(observeBody, 100);
          }
        };
        
        observeBody();
      },
      register(handler) {
        this.handlers.push(handler);
        if (!this.observer) {
          this.setup();
        }
      }
    };
  }
  
  // Register this module's handler
  window.__variantCardsObserverManager.register((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node instanceof Element) {
          // Find variant card galleries
          const variantCardGalleries = node.matches('[data-collection-variant-id] .card-gallery')
            ? [node]
            : Array.from(node.querySelectorAll('[data-collection-variant-id] .card-gallery'));
          
          // Find regular product card galleries with color swatches (filtered products)
          const regularProductCards = node.matches('product-card:not([data-collection-variant-id])')
            ? [node]
            : Array.from(node.querySelectorAll('product-card:not([data-collection-variant-id])'));
          
          /** @type {Element[]} */
          const regularCardGalleries = [];
          regularProductCards.forEach((card) => {
            const gallery = card.querySelector('.card-gallery');
            const variantPicker = card.querySelector('swatches-variant-picker-component');
            // Only include if it has a variant picker with swatches (color options)
            if (gallery && variantPicker) {
              regularCardGalleries.push(gallery);
            }
          });
          
          // Find recommendation card galleries with color swatches
          const recommendationCards = node.matches('.resource-list__item product-card, .resource-list__item .product-card')
            ? [node]
            : Array.from(node.querySelectorAll('.resource-list__item product-card, .resource-list__item .product-card'));
          
          /** @type {Element[]} */
          const recommendationCardGalleries = [];
          recommendationCards.forEach((card) => {
            const gallery = card.querySelector('.card-gallery');
            const variantPicker = card.querySelector('swatches-variant-picker-component');
            // Only include if it has a variant picker with swatches (color options)
            if (gallery && variantPicker) {
              recommendationCardGalleries.push(gallery);
            }
          });

          // Initialize all found galleries (variant cards + regular product cards + recommendation cards)
          [...variantCardGalleries, ...regularCardGalleries, ...recommendationCardGalleries].forEach((gallery) => {
            variantCardGallery.initCard(gallery);
          });
        }
      });
    });
  });
}

// Test if module is executing
export default variantCardGallery;




